import { useLocalStorageState } from 'ahooks'
import { memo, type ReactNode, useCallback } from 'react'
import { HiMinus, HiPlus } from 'react-icons/hi2'
import { twMerge } from 'tailwind-merge'

interface SectionProps {
  readonly defaultOpen?: boolean
  readonly name: string
  readonly title: string | ReactNode
  readonly description?: string | ReactNode
  readonly children: ReactNode
  readonly className?: string
  readonly hasValue?: boolean
}

export const Section = memo<SectionProps>(({ defaultOpen = false, name, title, description, hasValue = false, children, className }) => {
  const [open, setOpen] = useLocalStorageState<boolean>(`section-${name}`, {
    defaultValue: defaultOpen,
  })

  const handleToggle = useCallback(() => {
    setOpen((prev) => !prev)
  }, [setOpen])

  const buttonAriaLabel = typeof title === 'string' ? `Toggle ${title}` : 'Toggle section'
  const indicatorClasses = twMerge('w-[7px] h-[7px] rounded mr-2.5', hasValue ? 'bg-red-800' : 'bg-transparent')
  const contentClasses = twMerge('px-7 py-4 bg-white dark:bg-zinc-950 shadow-inner', className, open ? 'block' : 'hidden')

  return (
    <fieldset className='w-full'>
      <legend className='sr-only'>{typeof title === 'string' ? title : name}</legend>
      <button
        type='button'
        className='flex w-full items-center justify-between gap-3 py-2.5 pr-4 pl-3 dark:bg-zinc-900'
        onClick={handleToggle}
        aria-label={buttonAriaLabel}
        aria-expanded={open}
        aria-controls={`section-content-${name}`}
      >
        <div className='flex items-center'>
          <span className={indicatorClasses} aria-hidden='true' />
          <span className='text-base font-medium md:text-sm dark:font-normal'>{title}</span>
          {description && <span className='ml-1.5 text-sm/6 font-light'>({description})</span>}
        </div>
        {open ? <HiMinus className='size-4' aria-hidden='true' /> : <HiPlus className='size-4' aria-hidden='true' />}
      </button>
      {open && (
        <div id={`section-content-${name}`} className={contentClasses}>
          {children}
        </div>
      )}
    </fieldset>
  )
})
